import React from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import WorkspaceTab from "./settings/WorkspaceTab";
import BillingTab from "./settings/BillingTab";
import BrandKitTab from "./settings/BrandKitTab";
import AdvancedTab from "./settings/AdvancedTab";
import NotificationsTab from "./settings/NotificationsTab";
import PlatformKeysTab from "./settings/PlatformKeysTab";
import PrivacyTab from "./settings/PrivacyTab";

const TABS = [
  { id: "workspace", label: "Workspace" },
  { id: "billing", label: "Billing" },
  { id: "notifications", label: "Notifications" },
  { id: "privacy", label: "Privacy & Security" },
  { id: "platform-keys", label: "Platform Keys", adminOnly: true },
  { id: "brand", label: "Brand Kit" },
  { id: "advanced", label: "Advanced" },
];

// Backwards-compat: deep links to ?tab=general|members|integrations|sso resolve here.
const ALIASES = {
  general: "workspace",
  members: "workspace",
  integrations: "advanced",
  sso: "advanced",
};

export default function Settings() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("tab") || "workspace";
  const active = ALIASES[raw] || raw;
  const isOwner = user?.workspace_role === "owner";
  const [isAdmin, setIsAdmin] = React.useState(false);
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/me`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("dara_token") || localStorage.getItem("token") || ""}` },
    }).then(r => r.json()).then(d => setIsAdmin(Boolean(d?.is_super_admin))).catch(() => {});
  }, []);
  const visibleTabs = TABS.filter(t => !t.adminOnly || isAdmin);

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6" data-testid="settings-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · WORKSPACE SETTINGS</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase mt-1">Settings</h1>
        <p className="font-mono text-[11px] text-zinc-500 mt-2 max-w-xl leading-relaxed">
          Identity & members in Workspace, plan & seats in Billing, white-labelling in Brand Kit,
          and integrations + SSO in Advanced.
        </p>
      </div>

      <div className="border-b border-zinc-900 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-thin">
        <div className="flex items-center gap-1 sm:gap-2 min-w-max sm:min-w-0 sm:flex-wrap">
          {visibleTabs.map(t => (
            <button
              key={t.id}
              onClick={() => setSp({ tab: t.id })}
              data-testid={`tab-${t.id}`}
              className={`whitespace-nowrap font-mono text-[11px] uppercase tracking-widest px-3 sm:px-4 py-3 border-b-2 transition-colors min-h-[44px] ${
                active === t.id ? "text-[#FFB000] border-[#FFB000]" : "text-zinc-500 border-transparent hover:text-white"
              }`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {active === "workspace" && <WorkspaceTab isOwner={isOwner} />}
      {active === "billing" && <BillingTab />}
      {active === "notifications" && <NotificationsTab />}
      {active === "privacy" && <PrivacyTab />}
      {active === "platform-keys" && isAdmin && <PlatformKeysTab />}
      {active === "brand" && <BrandKitTab />}
      {active === "advanced" && <AdvancedTab isOwner={isOwner} />}
    </div>
  );
}
